export const API_URL =
  import.meta.env.VITE_API_URL ||
  "https://egab2-staging-1085999931581.us-central1.run.app"

export const API_URL_PREFIX = "/api/services/app"

export const TOKEN_NAME = "auth_token"
export const REFRESH_TOKEN_NAME = "refresh_token"
export const TOKEN_EXPIRES_AT_NAME = "auth_token_expires_at"
export const REFRESH_TOKEN_EXPIRES_AT_NAME = "refresh_token_expires_at"

const TOKEN_REFRESH_BUFFER = 60_000

export interface ApiErrorBody {
  message: string
  code?: string
  details?: unknown
  validationErrors?: Record<string, string[]>
}

export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  readonly details?: unknown
  readonly validationErrors?: Record<string, string[]>

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = "ApiError"
    this.status = status
    this.code = body.code
    this.details = body.details
    this.validationErrors = body.validationErrors
  }
  toJSON() {
    return {
      status: this.status,
      message: this.message,
      code: this.code,
      details: this.details,
      validationErrors: this.validationErrors,
    }
  }
}

type RefreshTokenResponse = {
  accessToken: string
  encryptedAccessToken: string
  expireInSeconds: number
}

let refreshPromise: Promise<string> | null = null

export function clearSession() {
  localStorage.removeItem(TOKEN_NAME)
  localStorage.removeItem(REFRESH_TOKEN_NAME)
  localStorage.removeItem(TOKEN_EXPIRES_AT_NAME)
  localStorage.removeItem(REFRESH_TOKEN_EXPIRES_AT_NAME)
}

function redirectToLogin() {
  clearSession()

  const redirect = `${window.location.pathname}${window.location.search}`

  window.location.replace(`/login?redirect=${encodeURIComponent(redirect)}`)
}

function getAccessToken() {
  return localStorage.getItem(TOKEN_NAME)
}

function shouldRefreshAccessToken() {
  const expiresAt = Number(localStorage.getItem(TOKEN_EXPIRES_AT_NAME))

  if (!expiresAt) {
    return false
  }

  return Date.now() >= expiresAt - TOKEN_REFRESH_BUFFER
}

function isRefreshTokenExpired() {
  const expiresAt = Number(localStorage.getItem(REFRESH_TOKEN_EXPIRES_AT_NAME))

  if (!expiresAt) {
    return false
  }

  return Date.now() >= expiresAt
}

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = performRefresh()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

async function performRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_NAME)

  if (!refreshToken || isRefreshTokenExpired()) {
    redirectToLogin()
    throw new ApiError(401, { message: "Session expired" })
  }

  const response = await fetch(`${API_URL}/api/TokenAuth/RefreshToken`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refreshToken,
    }),
  })

  if (!response.ok) {
    redirectToLogin()

    throw new ApiError(401, { message: "cannot refresh token" })
  }

  const data = (await response.json()) as RefreshTokenResponse

  const expiresAt = Date.now() + data.expireInSeconds * 1000

  localStorage.setItem(TOKEN_NAME, data.accessToken)

  localStorage.setItem(TOKEN_EXPIRES_AT_NAME, expiresAt.toString())

  return data.accessToken
}

async function executeRequest(
  endpoint: string,
  options: RequestInit,
  token: string | null
) {
  const isFormData = options.body instanceof FormData

  return fetch(`${API_URL}${endpoint}`, {
    ...options,

    headers: {
      ...(!isFormData && { "Content-Type": "application/json" }),

      ...(token && {
        Authorization: `Bearer ${token}`,
      }),

      ...options.headers,
    },
  })
}
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  let token = getAccessToken()

  /*
   * Proactively refresh if the token is about to expire.
   */
  if (token && shouldRefreshAccessToken()) {
    token = await refreshAccessToken()
  }

  let response = await executeRequest(endpoint, options, token)

  /*
   * The backend may reject the token before our
   * local expiry calculation says it's expired.
   *
   * Refresh once and retry the original request.
   */
  if (response.status === 401 && localStorage.getItem(REFRESH_TOKEN_NAME)) {
    token = await refreshAccessToken()

    response = await executeRequest(endpoint, options, token)
  }

  let body: unknown = null

  const contentType = response.headers.get("content-type")

  if (contentType?.includes("application/json")) {
    body = await response.json()
  }

  if (!response.ok) {
    if (response.status === 401) {
      redirectToLogin()
    }

    throw new ApiError(response.status, body.error as ApiErrorBody).toJSON()
  }

  return body as T
}

export const apiClient = {
  get: <T>(url: string) => request<T>(url),

  post: <T>(url: string, body?: unknown) =>
    request<T>(url, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  postFiles: <T>(url: string, formData: FormData) =>
    request<T>(url, {
      method: "POST",
      body: formData,
    }),

  put: <T>(url: string, body?: unknown) =>
    request<T>(url, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  patch: <T>(url: string, body?: unknown) =>
    request<T>(url, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  delete: <T>(url: string) =>
    request<T>(url, {
      method: "DELETE",
    }),
}
